<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import OrganizationsGridActions from '@/components/features/admin/OrganizationsGridActions.vue'
import { useOrganizationAdminStore, type RegionItem } from '@/stores/useOrganizationAdminStore'
import { useUserAdminStore } from '@/stores/useUserAdminStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { ArrowLeftIcon, PlusIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const store = useOrganizationAdminStore()
const adminStore = useUserAdminStore()
const boostCoreStore = useBoostCorePermissionStore()
const showModal = ref(false)
const editingItem = ref<RegionItem | null>(null)
const formName = ref('')
const formBu = ref<string | null>(null)
const saving = ref(false)
const errorMsg = ref<string | null>(null)

const BU_OPTIONS = computed(() => [
  { id: 'none', value: null, label: '—' } as { id: string; value: string | null; label: string },
  ...adminStore.bus,
])

const gridColumns = computed(() => [
  { field: 'branchName', headerName: 'BU', valueFormatter: (p: any) => p.value ?? '—', filter: false },
  { field: 'name', headerName: 'Nom', filter: false },
  { field: 'userCount', headerName: 'Nb utilisateurs', filter: false },
  {
    field: 'actions',
    headerName: 'Actions',
    cellRenderer: OrganizationsGridActions,
    cellRendererParams: { onClickEdit: openEdit },
    sortable: false,
    cellClass: 'border-0 grid-action',
    minWidth: 80,
    maxWidth: 80,
  },
])

function openEdit(item: RegionItem) {
  editingItem.value = item
  formName.value = item.name
  formBu.value = item.branchName
  showModal.value = true
  errorMsg.value = null
}

function openCreate() {
  editingItem.value = null
  formName.value = ''
  formBu.value = null
  showModal.value = true
  errorMsg.value = null
}

function closeModal() {
  showModal.value = false
  editingItem.value = null
}

async function save() {
  saving.value = true
  errorMsg.value = null
  try {
    if (editingItem.value) {
      const ok = await store.updateRegion(editingItem.value.uuid, { name: formName.value, bu: formBu.value })
      if (ok) closeModal()
      else errorMsg.value = 'Erreur lors de la mise à jour'
    } else {
      const ok = await store.createRegion({ name: formName.value, bu: formBu.value })
      if (ok) closeModal()
      else errorMsg.value = 'Erreur lors de la création'
    }
  } finally {
    saving.value = false
  }
}

function goBack() {
  router.push({ name: 'admin-users' })
}

onMounted(async () => {
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  await Promise.all([store.fetchRegions(), adminStore.fetchBus()])
})
</script>

<template>
  <div class="p-6">
    <div class="flex items-center gap-4 mb-4">
      <button class="btn btn-ghost btn-sm" @click="goBack">
        <ArrowLeftIcon class="w-5 h-5" />
      </button>
      <Bs2Title title="Régions" subtitle="Administration" />
    </div>
    <div class="mt-8 card bg-base-100 shadow">
      <div class="card-body">
        <GridVue :list="store.regions" :cols="gridColumns">
          <template #title>
            <div class="flex items-center justify-between w-full">
              <h2 class="card-title">Régions ({{ store.regions.length }})</h2>
              <ButtonDaisy text="Nouvelle région" size="sm" :icon="PlusIcon" @click="openCreate" />
            </div>
          </template>
        </GridVue>
      </div>
    </div>
  </div>

  <div class="modal" :class="{ 'modal-open': showModal }">
    <div class="modal-box">
      <h3 class="font-bold text-lg">{{ editingItem ? 'Modifier la région' : 'Nouvelle région' }}</h3>
      <div v-if="errorMsg" class="alert alert-error mt-4">{{ errorMsg }}</div>
      <div class="mt-4 space-y-4">
        <SelectInput v-model="formBu" label="BU" :options="BU_OPTIONS" />
        <div class="form-control">
          <label class="label"><span class="label-text">Nom</span></label>
          <input v-model="formName" type="text" class="input input-bordered" placeholder="Nom de la région" />
        </div>
      </div>
      <div class="modal-action">
        <button class="btn" @click="closeModal">Annuler</button>
        <button class="btn btn-primary" :disabled="saving || !formName.trim()" @click="save">
          {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
        </button>
      </div>
    </div>
    <div class="modal-backdrop" @click="closeModal" />
  </div>
</template>
